<script setup>
import {ref, computed, watch } from "vue";
import { CheckIcon, SelectorIcon } from '@heroicons/vue/solid'
import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxLabel,
  ComboboxOption,
  ComboboxOptions,
} from '@headlessui/vue'
import { Inertia } from "@inertiajs/inertia";
import AddressPicker from "../Partials/AddressPicker";
import { useForm, usePage } from "@inertiajs/inertia-vue3";
import Cart from "../../../Shared/Cart";
import AddressForm from "../Partials/AddressForm";

const emit = defineEmits([
  'update:selectedCustomer',
  'onSuccess',
])

const props = defineProps({
  customers: Object,
  addresses: Object,
  reservations: Object,
  selectedCustomer: Object,
})

const shippingAddress = ref()
const billingAddress = ref()
const separate_billing_address = ref(false)
const cart = new Cart();
const orderForCustomer = ref(false);
const orderForGuest = ref(false);
const orderForEmployee = ref(false);

const user = computed(() => usePage().props.value.auth.user)

const customerQuery = ref('')
const selectedCustomer = ref( null)
const filteredCustomers = computed(() =>
  customerQuery.value === ''
    ? props.customers
    : props.customers.filter((customer) => {
      return customer.name.toLowerCase().includes(customerQuery.value.toLowerCase())
    })
)

watch(selectedCustomer, async (newSelectedCustomer, oldSelectedCustomer) => {
  if (newSelectedCustomer !== oldSelectedCustomer) {
    shippingAddress.value = null;
    billingAddress.value = null;
    emit('update:selectedCustomer', newSelectedCustomer)
    Inertia.get('/checkout', {
      customer_id: newSelectedCustomer.id
    }, {
      preserveState: true
    })
  }
})

function setOrderForCustomer() {
  orderForCustomer.value = true;
  orderForGuest.value = false;
}

function setOrderForGuest() {
  orderForGuest.value = true;
  orderForCustomer.value = false;
}

function orderAsEmployee() {
  orderForGuest.value = false;
  orderForCustomer.value = false;
  orderForEmployee.value = true;

  if(usePage().props.value.auth.user.addresses.length > 0) {
    submitForm();
  }
}

function formReady() {
  if(separate_billing_address.value){
    return shippingAddress.value == null  || selectedCustomer.value == null || billingAddress.value == null;
  }
  return shippingAddress.value == null  || selectedCustomer.value == null;
}

const form = useForm({
  reservations: [],
})

function submitForm() {
  form.reservations = props.reservations;
  form.transform((data) => ({
    ...data,
    order_for_employee: orderForEmployee.value,
    customer: selectedCustomer.value,
    shippingAddress: shippingAddress.value,
    billingAddress: separate_billing_address.value ?
      billingAddress.value :
      shippingAddress.value
  })).post(route('employee-checkout'), {
    onSuccess: () => {
      cart.clearItems()
    }
  });
}

</script>

<template>
  <form @submit.prevent="submitForm">

    <div @click="setOrderForCustomer()" class="my-5 w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:opacity-80 focus:outline-none cursor-pointer">
      <span v-text="'Bestel voor bestaande klant'"></span>
    </div>

    <div @click="setOrderForGuest()" class="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:opacity-80 focus:outline-none cursor-pointer">
      <span v-text="'Bestel voor gast'"></span>
    </div>

    <div @click="orderAsEmployee()"
         class="my-5 w-full flex justify-center py-2 px-4 border rounded-md shadow-sm text-sm font-medium  hover:opacity-80 focus:outline-none"
         :class="{'bg-white': user.addresses.length === 0, 'text-white bg-primary border-transparent cursor-pointer': user.addresses.length > 0 }"
    >
      <span v-text="user.addresses.length === 0 ? 'Maak een adres aan om bestellen als medewerker' : 'Bestel als medewerker'"></span>
    </div>

    <div v-if="orderForCustomer">
      <Combobox as="div" v-model="selectedCustomer" class="py-4">
        <ComboboxLabel class="block text-sm font-medium text-gray-700">Klant</ComboboxLabel>
        <div class="relative mt-1">
          <ComboboxInput class="w-full rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 shadow-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary sm:text-sm"
                         @change="customerQuery = $event.target.value"
                         :display-value="(customer) => customer?.name " />
          <ComboboxButton class="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
            <SelectorIcon class="h-5 w-5 text-gray-400" aria-hidden="true" />
          </ComboboxButton>

          <ComboboxOptions v-if="filteredCustomers.length > 0" class="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
            <ComboboxOption v-for="customer in filteredCustomers" :key="customer.id" :value="customer" as="template" v-slot="{ active, selected }">
              <li :class="['relative cursor-default select-none py-2 pl-3 pr-9', active ? 'bg-primary text-white' : 'text-gray-900']">
                      <span :class="['block truncate', selected && 'font-semibold']">
                        {{ customer.name}}
                      </span>
                <span :class="['truncate text-gray-500', active ? 'text-white' : 'text-gray-500']">
                        {{ customer.email }}
                      </span>
                <span v-if="selected" :class="['absolute inset-y-0 right-0 flex items-center pr-4', active ? 'text-white' : 'text-indigo-600']">
                <CheckIcon class="h-5 w-5" aria-hidden="true" />
              </span>
              </li>
            </ComboboxOption>
          </ComboboxOptions>
        </div>
      </Combobox>

      <address-picker :key="selectedCustomer" v-model="shippingAddress" :addresses="props.addresses">
        <template #label>
          Selecteer klant gegevens
        </template>
      </address-picker>

      <div class="mt-1 mb-2 flex items-center">
        <input v-model="separate_billing_address" id="same-as-shipping" name="same-as-shipping" type="checkbox" class="h-4 w-4 border-gray-300 rounded text-primary focus:ring-primary">
        <div class="ml-2">
          <label for="same-as-shipping" class="text-sm font-medium text-gray-900">Afwijkende factuur gegevens </label>
        </div>
      </div>

      <address-picker v-if="separate_billing_address" :key="selectedCustomer" v-model="billingAddress" :addresses="props.addresses">
        <template #label>
          Selecteer factuur gegevens
        </template>
      </address-picker>

      <VTooltip :disabled="!formReady()" theme="info" placement="bottom">

        <button type="submit" :disabled="form.processing || formReady()" class="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:opacity-80 focus:outline-none">
          <svg v-if="form.processing" class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span v-text="form.processing ? 'Bezig...' : 'Bevestig reservering'"></span>
        </button>

        <template #popper >
          Selecteer een klant en adres om de bestelling te plaatsen.
        </template>
      </VTooltip>
    </div>

    <div v-if="orderForGuest">
      <AddressForm v-model="shippingAddress" :errors="form.errors" errors-name="shippingAddress">
        <template v-if="separate_billing_address" #title>
          Gegevens van client
        </template>
      </AddressForm>

      <div class="mt-4 mb-4 flex items-center">
        <input v-model="separate_billing_address" id="same-as-shipping" name="same-as-shipping" type="checkbox" class="h-4 w-4 border-gray-300 rounded text-primary focus:ring-primary">
        <div class="ml-2">
          <label for="same-as-shipping" class="text-sm font-medium text-gray-900">Gegevens van zorgverlener toevoegen</label>
        </div>
      </div>

      <AddressForm v-if="separate_billing_address" v-model="billingAddress" :errors="form.errors" errors-name="billingAddress">
        <template #title>
          Gegevens van zorgverlener
        </template>
      </AddressForm>

      <button type="submit" :disabled="form.processing" class="w-full flex justify-center py-2 px-4 mt-4 mb-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:opacity-80 focus:outline-none cursor-pointer">
        <svg v-if="form.processing" class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <span v-text="form.processing ? 'Bezig...' : 'Bevestig reservering'"></span>
      </button>

    </div>

  </form>
</template>
